// Grids & Gantts — editing a generator where it is printed.
//
// This is the behaviour a structure has: the amber lines in the tree are
// not a legend, they are the RULES, and clicking one opens that rule. An
// admin who can see "Insert work items: …" should be able to change it
// there, not go looking for it in a settings page that lists every rule of
// every folder and makes them find the right one again.
//
// Each amber row carries an `edit` descriptor saying which rule it is —
// `{ what, folderId, index }` — and this turns that into the few controls
// that rule actually has. Nothing else: a sort rule gets a sort dropdown,
// not a form with an empty ZQL box on it.
//
// It writes into the page's DRAFT config, exactly as Page settings does, so
// the change previews immediately and is kept by the same Save button. A
// reader never sees any of this: the amber rows are admin-only, and so is
// this.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FONT } from './theme';
import { FIELD_SORT, SORTS, descendantFolderIds, newFolder, newInsert, newLink, removeFolderQuestion, sortFieldOf, withoutRule } from './config';
import { FOLDER_ICONS, iconSpec } from './icons';

// A SORT PICKER: the built-in orders, or "A field…" with the field's name —
// stored as `field:<name>` (see ./config.js).
function SortPick({ value, onChange, small }) {
  const field = sortFieldOf(value);
  const isField = !!field || value === FIELD_SORT;
  return (
    <>
      <select value={isField ? FIELD_SORT : value} onChange={(e) => onChange(e.target.value === FIELD_SORT ? `${FIELD_SORT}${field}` : e.target.value)} style={{ ...small, width: '100%' }}>
        {SORTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        <option value={FIELD_SORT}>A field…</option>
      </select>
      {isField ? (
        <input value={field} list="wip-fields" autoFocus placeholder="Field name — numbers as numbers, dates as dates, text A to Z; empty last"
          onChange={(e) => onChange(`${FIELD_SORT}${e.target.value}`)} style={{ ...small, width: '100%', marginTop: 6 }} />
      ) : null}
    </>
  );
}
import { DATE_GRAINS, FORMULA_HELP, GROUP_KINDS, PRESETS, normalizeGroup } from './grouping';
import { FILTER_MODES } from './structure';

export default function RuleEditor({ edit, draft, setDraft, ui, S, testZql, fieldList, onClose, sample = null, previewGroup = null, workTypes = null, headingsOf = null }) {
  const ref = useRef(null);
  const [test, setTest] = useState('');
  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [onClose]);

  if (!edit) return null;
  const { what, folderId, index, parentId } = edit;
  const fi = draft.folders.findIndex((f) => f.id === folderId);
  const folder = fi >= 0 ? draft.folders[fi] : null;

  const set = (patch) => setDraft({ ...draft, ...patch });
  const setFolder = (patch) => set({ folders: draft.folders.map((f, j) => (j === fi ? { ...f, ...patch } : f)) });
  const setExpand = (patch) => setFolder({ expand: { ...folder.expand, ...patch } });
  const setLink = (patch) => setExpand({ links: folder.expand.links.map((l, j) => (j === index ? { ...l, ...patch } : l)) });

  // EVERY RULE THAT CAN GO HAS A REMOVE, in the same place: the foot of its
  // own editor. Adding and editing were both one click from the amber line;
  // removing was missing from most of them, or hidden behind "Add a rule".
  // Like every change here it is made to the DRAFT — nothing is gone for
  // anybody until Save, and leaving without saving brings it all back.
  //
  // What a removal does is said once, in config.js (withoutRule) — the bin
  // beside the amber line removes through it too. A folder takes the folders
  // inside it with it ("the whole folder"), after asking.
  const remove = (which = edit) => {
    const out = withoutRule(draft, which);
    if (!out) return;
    // eslint-disable-next-line no-alert
    if (out.folder && !window.confirm(removeFolderQuestion(out.folder))) return;
    setDraft(out.draft);
    onClose();
  };
  const removeBtn = (label, run) => (
    <div style={{ borderTop: `1px solid ${ui.border}`, marginTop: 12, paddingTop: 8 }}>
      <button onClick={run} style={{ ...S.exportBtn, padding: '3px 9px', fontSize: 11.5, color: ui.red }}>{label}</button>
    </div>
  );

  const small = { ...S.input, padding: '5px 8px', fontSize: 12.5 };
  const mono = { ...small, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' };
  const btn = { ...S.exportBtn, padding: '3px 9px', fontSize: 11.5 };
  const row = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' };
  const lbl = { ...S.tiny, fontWeight: 700, marginBottom: 4 };

  const runTest = async (zql) => {
    if (!zql || !zql.trim()) return;
    setTest('testing…');
    try { const n = await testZql(zql); setTest(`${Number(n).toLocaleString()} match`); }
    catch (e) { setTest(`⚠ ${(e && e.message) || 'failed'}`); }
  };
  const zqlBox = (value, onChange, hint) => (
    <>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4}
        style={{ ...mono, width: '100%', resize: 'vertical', lineHeight: 1.45 }} />
      <div style={{ ...row, marginTop: 6 }}>
        <button onClick={() => runTest(value)} style={btn}>Test</button>
        <span style={S.tiny}>{test}</span>
        <span style={{ ...S.tiny, flex: '1 1 100%' }}>{hint}</span>
      </div>
    </>
  );

  let title = 'Rule';
  let body = null;

  if (what === 'filter') {
    title = 'Filter work items — the whole structure';
    body = (
      <>
        {zqlBox(draft.filterZql, (v) => set({ filterZql: v }), 'Every level of every folder is passed through this. Blank turns it off.')}
        <div style={lbl}>What it means</div>
        <select value={draft.filterMode} onChange={(e) => set({ filterMode: e.target.value })} style={{ ...small, width: '100%' }}>
          {FILTER_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        {draft.filterZql.trim() ? removeBtn('Remove this filter', () => remove()) : null}
      </>
    );
  } else if (what === 'sortRoot') {
    title = 'Sort — the top of every folder';
    body = (
      <SortPick value={draft.sortRoot} onChange={(v) => set({ sortRoot: v })} small={small} />
    );
  } else if (what === 'addFolder') {
    // A folder is a box with its own rules. It is created EMPTY and named,
    // because the one thing it cannot be given here is its insert query —
    // that wants a ZQL box and a Test, which is the insert rule's own
    // editor, one click away once the folder exists.
    const into = parentId ? draft.folders.find((x) => x.id === parentId) : null;
    title = into ? `Add a folder inside ${into.name}` : 'Add a folder';
    body = (
      <>
        <div style={S.tiny}>
          A folder is a box with its own rules: its own insert query, its own
          extenders, its own groupings. {into
            ? <>It appears inside <strong style={{ color: ui.fg }}>{into.name}</strong>, whose counts take in everything it holds; its rules are the amber lines inside it.</>
            : <>It appears at the bottom of the structure; its rules are the amber lines inside it. Drag its row onto another folder to put it inside that one.</>}
        </div>
        <div style={{ ...row, marginTop: 10 }}>
          <button
            onClick={() => {
              const f = newFolder(into ? into.id : null);
              setDraft({ ...draft, folders: [...draft.folders, f] });
              onClose();
            }}
            style={{ ...S.pill(true) }}>Add it</button>
          <button onClick={onClose} style={btn}>Cancel</button>
        </div>
      </>
    );
  } else if (what === 'addRule') {
    const f2 = draft.folders.find((x) => x.id === folderId);
    title = f2 ? `Add a rule to ${f2.name}` : 'Add a rule';
    const addTo = (patch) => {
      setDraft({ ...draft, folders: draft.folders.map((x) => (x.id === folderId ? { ...x, ...patch } : x)) });
      onClose();
    };
    body = f2 ? (
      <>
        <div style={{ ...S.tiny, marginBottom: 10 }}>
          Every rule below is scoped to this folder alone. The structure-wide
          filter and sort are the amber lines at the very top.
        </div>
        <div style={{ display: 'grid', gap: 7 }}>
          {/* FIRST, because it is the only rule that puts work items INTO a
              folder — everything else decides what happens to them once they
              are there. A folder routinely needs two of these: two queries
              that between them say what belongs in the box, where no single
              query can. The menu never offered it, so that folder could not
              be built without inventing a second folder beside it. */}
          <button style={btn} onClick={() => addTo({ inserts: [...(f2.inserts || []), newInsert()] })}>
            ✛ Insert work items… <span style={S.tiny}>— a query whose answers become rows in this folder</span>
          </button>
          <button style={btn} onClick={() => addTo({ expand: { ...f2.expand, links: [...f2.expand.links, newLink()] } })}>
            ⛓ Add work items linked by… <span style={S.tiny}>— a link type and the relation its parent side uses</span>
          </button>
          {!f2.expand.byParent ? (
            <button style={btn} onClick={() => addTo({ expand: { ...f2.expand, byParent: true } })}>
              ⛓ Child work items using the Parent field
            </button>
          ) : null}
          {!f2.expand.lines ? (
            <button style={btn} onClick={() => addTo({ expand: { ...f2.expand, lines: { field: '', types: [], label: 'Line', key: '', text: '', skip: [], map: [] } } })}>
              ▤ Table lines as rows… <span style={S.tiny}>— the lines of a table in a work item&rsquo;s field, drawn under it</span>
            </button>
          ) : null}
          <button style={btn} onClick={() => addTo({ groups: [...f2.groups, { id: `g${Date.now().toString(36)}`, kind: 'field', field: '' }] })}>
            ▦ Group by… <span style={S.tiny}>— brackets what this folder inserted</span>
          </button>
          {!f2.groupChildren ? (
            <button style={btn} onClick={() => { setDraft({ ...draft, folders: draft.folders.map((x) => (x.id === folderId ? { ...x, groupChildren: { field: '', types: [] } } : x)) }); onClose(); }}>
              ▦ Group the children by… <span style={S.tiny}>— under every row, its children bracketed by a field</span>
            </button>
          ) : null}
          {!f2.filterZql.trim() ? (
            <button style={btn} onClick={() => addTo({ filterZql: 'status not in (ARCHIVE)' })}>
              ▼ Filter work items, in this folder only
            </button>
          ) : null}
        </div>
        <div style={{ borderTop: `1px solid ${ui.border}`, marginTop: 12, paddingTop: 8 }}>
          <button style={{ ...btn, color: ui.red }} onClick={() => remove({ what: 'folder', folderId })}>
            Remove this whole folder
          </button>
        </div>
      </>
    ) : <div style={S.tiny}>That folder is no longer here.</div>;
  } else if (!folder) {
    body = <div style={S.tiny}>That folder is no longer here.</div>;
  } else if (what === 'insert') {
    const list = folder.inserts || [];
    const at = typeof index === 'number' ? index : 0;
    const q = list[at];
    if (!q) { body = <div style={S.tiny}>That insert rule is no longer here.</div>; } else {
      title = list.length > 1
        ? `Insert work items (${at + 1} of ${list.length}) — ${folder.name}`
        : `Insert work items — ${folder.name}`;
      const setInsert = (v) => setFolder({ inserts: list.map((x, j) => (j === at ? { ...x, zql: v } : x)) });
      body = (
        <>
          {zqlBox(q.zql, setInsert, list.length > 1
            ? 'The answers to this query join the folder’s top-level rows. A work item two generators both find is drawn once.'
            : 'The answers to this query are the folder’s top-level rows.')}
          {removeBtn(list.length > 1 ? 'Remove this insert rule' : 'Remove this insert rule — the folder is empty until it has one',
            () => remove())}
        </>
      );
    }
  } else if (what === 'folderFilter') {
    title = `Filter work items — ${folder.name} only`;
    body = (
      <>
        {zqlBox(folder.filterZql, (v) => setFolder({ filterZql: v }), 'On top of the structure filter. Blank = none.')}
        {removeBtn('Remove this filter', () => remove())}
      </>
    );
  } else if (what === 'sort') {
    title = `Sort — under a parent in ${folder.name}`;
    body = (
      <SortPick value={folder.sort} onChange={(v) => setFolder({ sort: v })} small={small} />
    );
  } else if (what === 'parent') {
    title = `Child work items using the Parent field — ${folder.name}`;
    body = (
      <>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center', marginBottom: 8 }}>
          <input type="checkbox" checked={!!folder.expand.byParent} onChange={(e) => setExpand({ byParent: e.target.checked })} />
          Use the Parent field to find what hangs under a row
        </label>
        <div style={lbl}>How deep the folder goes</div>
        <input type="number" min="1" max="20" value={folder.expand.maxDepth}
          onChange={(e) => setExpand({ maxDepth: Number(e.target.value) })} style={{ ...small, width: 100 }} />
        {removeBtn('Remove this rule', () => remove())}
      </>
    );
  } else if (what === 'lines') {
    // TABLE LINES AS ROWS (./lines.js): which field holds the table, under
    // which work types, what a line is called, which of its columns make
    // its name, which lines stay out, and which of the grid's fields read
    // which of its columns.
    const L = folder.expand.lines || { field: '', types: [], label: 'Line', key: '', text: '', skip: [], map: [] };
    const setL = (patch) => setExpand({ lines: { ...L, ...patch } });
    const heads = headingsOf && L.field ? headingsOf(L.field, L.types) : [];
    const pairRows = (listName, a, b, aPh, bPh, aList) => (
      <>
        {(L[listName] || []).map((x, j) => (
          <div key={j} style={{ ...row, marginBottom: 4 }}>
            <input value={x[a] || ''} list={aList} placeholder={aPh} style={{ ...small, flex: '1 1 40%' }}
              onChange={(e) => setL({ [listName]: L[listName].map((y, k) => (k === j ? { ...y, [a]: e.target.value } : y)) })} />
            <span style={S.tiny}>{listName === 'map' ? '←' : '='}</span>
            <input value={x[b] || ''} list={listName === 'map' ? 'wip-line-heads' : undefined} placeholder={bPh} style={{ ...small, flex: '1 1 40%' }}
              onChange={(e) => setL({ [listName]: L[listName].map((y, k) => (k === j ? { ...y, [b]: e.target.value } : y)) })} />
            <button onClick={() => setL({ [listName]: L[listName].filter((_, k) => k !== j) })} style={{ ...btn, color: ui.red }} title="Take this one out">✕</button>
          </div>
        ))}
        <button onClick={() => setL({ [listName]: [...(L[listName] || []), { [a]: '', [b]: '' }] })} style={{ ...btn, marginBottom: 8 }}>+ Add</button>
      </>
    );
    title = `Table lines as rows — ${folder.name}`;
    body = (
      <>
        <datalist id="wip-line-heads">{heads.map((h) => <option key={h} value={h} />)}</datalist>
        <div style={{ ...S.tiny, marginBottom: 10 }}>
          Under every work item of these types, each line of the table in this field is drawn as a row. A line is
          not a work item: it opens, edits and counts as nothing — its cells read the table.
        </div>
        <div style={lbl}>The field that holds the table</div>
        <input value={L.field} list="wip-fields" onChange={(e) => setL({ field: e.target.value })} placeholder="e.g. Part Routing Material" style={{ ...small, width: '100%', marginBottom: 8 }} />
        {L.field ? (
          <div style={{ ...S.tiny, marginBottom: 8 }}>
            {heads.length ? <>Its table&rsquo;s columns: <strong>{heads.join(' · ')}</strong></> : 'No table found in this field on the loaded work items yet (Save and ↻ to load it).'}
          </div>
        ) : null}
        <div style={lbl}>Only under these work types (blank = all)</div>
        <input defaultValue={(L.types || []).join(', ')} key={(L.types || []).join(',')} placeholder="e.g. Work Instruction"
          onBlur={(e) => setL({ types: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} style={{ ...small, width: '100%', marginBottom: 8 }} />
        <div style={lbl}>What a line is called (its Work type column)</div>
        <input value={L.label} onChange={(e) => setL({ label: e.target.value })} placeholder="e.g. Part" style={{ ...small, width: '100%', marginBottom: 8 }} />
        <div style={lbl}>Its name — the column that names it, then the one that describes it</div>
        <div style={row}>
          <input value={L.key} list="wip-line-heads" onChange={(e) => setL({ key: e.target.value })} placeholder="e.g. Component No." style={{ ...small, flex: '1 1 45%' }} />
          <input value={L.text} list="wip-line-heads" onChange={(e) => setL({ text: e.target.value })} placeholder="e.g. Object Description" style={{ ...small, flex: '1 1 45%' }} />
        </div>
        <div style={lbl}>Leave out the lines where a column holds a value</div>
        {pairRows('skip', 'column', 'value', 'Column, e.g. ASM', 'Value, e.g. Yes', 'wip-line-heads')}
        <div style={lbl}>The grid&rsquo;s fields a line fills — field ← the table&rsquo;s column</div>
        {pairRows('map', 'field', 'column', 'Field, e.g. Part Number', 'Column, e.g. Component No.', 'wip-fields')}
        <div style={{ ...S.tiny, marginBottom: 4 }}>
          A column of the grid reading one of these fields shows the line&rsquo;s value on a line row — so a Part Number
          column, and an External database column matching on Part Number, work for lines too.
        </div>
        {removeBtn('Remove this rule', () => remove())}
      </>
    );
  } else if (what === 'childGroups') {
    // GROUP THE CHILDREN: under every row, its children of these work types
    // bracketed by the field's value — the folder's own groupings bracket
    // only what it inserted.
    const G = folder.groupChildren || { field: '', types: [] };
    const setG = (patch) => setDraft({ ...draft, folders: draft.folders.map((x) => (x.id === folder.id ? { ...x, groupChildren: { ...G, ...patch } } : x)) });
    title = `Group the children — ${folder.name}`;
    body = (
      <>
        <div style={{ ...S.tiny, marginBottom: 10 }}>
          Under every row, its children of these work types sit under one header per value of the field (blank values under
          &ldquo;(none)&rdquo;); its other children stay above the headers. A header adds up what is under it, like any group.
        </div>
        <div style={lbl}>The field the children are grouped by</div>
        <input value={G.field} list="wip-fields" onChange={(e) => setG({ field: e.target.value })} placeholder="e.g. SAP Storage Location" style={{ ...small, width: '100%', marginBottom: 8 }} />
        <div style={lbl}>Only children of these work types (blank = all)</div>
        <input defaultValue={(G.types || []).join(', ')} key={(G.types || []).join(',')} placeholder="e.g. Work Instruction"
          onBlur={(e) => setG({ types: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} style={{ ...small, width: '100%', marginBottom: 8 }} />
        {removeBtn('Remove this rule', () => remove())}
      </>
    );
  } else if (what === 'link') {
    const link = folder.expand.links[index];
    if (!link) { body = <div style={S.tiny}>That extender is no longer here.</div>; } else {
      title = `Add work items linked by ${link.type} — ${folder.name}`;
      body = (
        <>
          <div style={lbl}>Link type</div>
          <input value={link.type} onChange={(e) => setLink({ type: e.target.value })} placeholder="Link" style={{ ...small, marginBottom: 8 }} />
          <div style={lbl}>The relation, as the PARENT’s side words it</div>
          <input value={link.relation} onChange={(e) => setLink({ relation: e.target.value })} placeholder="Link to Child" style={{ ...small, marginBottom: 8 }} />
          <div style={lbl}>Levels it applies at</div>
          <div style={row}>
            <input type="number" min="1" max="20" value={link.levelFrom} onChange={(e) => setLink({ levelFrom: Number(e.target.value) })} style={{ ...small, width: 74 }} />
            <span style={S.tiny}>to</span>
            <input type="number" min="1" max="20" value={link.levelTo} onChange={(e) => setLink({ levelTo: Number(e.target.value) })} style={{ ...small, width: 74 }} />
          </div>
          <button onClick={() => remove()} style={{ ...btn, color: ui.red }}>Remove this extender</button>
        </>
      );
    }
  } else if (what === 'group') {
    const g = normalizeGroup(folder.groups[index], index);
    if (!folder.groups[index]) { body = <div style={S.tiny}>That grouping is no longer here.</div>; } else {
      title = `Group by — ${folder.name}`;
      // FIVE KINDS OF GROUPING, one editor. Whatever the kind, the rows are
      // bracketed the same way; what changes is how a row's group is found.
      const setG = (patch) => setFolder({ groups: folder.groups.map((x, j) => (j === index ? { ...normalizeGroup(x, j), ...patch } : x)) });
      const setBucket = (bi, patch) => setG({ buckets: g.buckets.map((b, j) => (j === bi ? { ...b, ...patch } : b)) });
      const preview = previewGroup && sample ? (() => { try { return previewGroup(g, folder.id); } catch (e) { return `(${(e && e.message) || 'error'})`; } })() : '';
      body = (
        <>
          <div style={lbl}>Group by</div>
          <select value={g.kind} onChange={(e) => setG({ kind: e.target.value })} style={{ ...small, marginBottom: 8, width: 360 }}>
            {GROUP_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          {g.kind === 'field' ? (
            <>
              <div style={lbl}>Field</div>
              <input value={g.field} list="wip-fields" onChange={(e) => setG({ field: e.target.value })} placeholder="Field name" style={{ ...small, marginBottom: 8 }} />
              <div style={{ ...S.tiny, marginBottom: 8 }}>
                “Space category”, “Project name” and “Project” are the structure’s own —
                they live on the project, not on the work item. Headers sort A to Z
                (Status: To do → In progress → Done, then A to Z) unless you give an order.
              </div>
              <div style={lbl}>Header order (optional) — one per line; the rest follow A to Z</div>
              <textarea defaultValue={(g.order || []).join('\n')} key={`${folder.id}:${g.id}:order`} rows={3} spellCheck={false}
                onBlur={(e) => setG({ order: e.target.value.split(/\n|,/).map((x) => x.trim()).filter(Boolean) })}
                placeholder={'To Do\nIn Review\nApproved'} style={{ ...small, width: '100%', resize: 'vertical', marginBottom: 8 }} />
            </>
          ) : null}
          {g.kind === 'date' ? (
            <>
              <div style={lbl}>Date field</div>
              <div style={row}>
                <input value={g.field} list="wip-fields" onChange={(e) => setG({ field: e.target.value })} placeholder="Due date, Start date, Created, Resolved…" style={{ ...small, flex: '1 1 240px' }} />
                <select value={g.grain} onChange={(e) => setG({ grain: e.target.value })} style={{ ...small, width: 130 }}>
                  {DATE_GRAINS.map((d) => <option key={d.id} value={d.id}>by {d.label.toLowerCase()}</option>)}
                </select>
              </div>
              <div style={{ ...S.tiny, marginBottom: 8 }}>Headers read “Mar 2026”, “Q1 2026”, “Week 12, 2026”… and sort in date order; work items without a date come last under “(none)”.</div>
            </>
          ) : null}
          {g.kind === 'preset' ? (
            <>
              <div style={lbl}>Grouping</div>
              <select value={g.preset} onChange={(e) => setG({ preset: e.target.value })} style={{ ...small, marginBottom: 8, width: 420 }}>
                {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <div style={{ ...S.tiny, marginBottom: 8 }}>Ready-made, nothing to configure. The due bands read today’s date when the page loads.</div>
            </>
          ) : null}
          {g.kind === 'zql' ? (
            <>
              <div style={lbl}>Buckets, in order — a work item lands in the FIRST one it matches</div>
              {g.buckets.map((b, bi) => (
                <div key={b.id} style={row}>
                  <input value={b.label} onChange={(e) => setBucket(bi, { label: e.target.value })} placeholder="Header" style={{ ...small, flex: '0 1 170px' }} />
                  <input value={b.zql} onChange={(e) => setBucket(bi, { zql: e.target.value })} placeholder="ZQL — e.g. duedate < startOfDay()" style={{ ...mono, flex: '1 1 260px' }} />
                  <button onClick={() => runTest(b.zql)} style={btn}>Test</button>
                  <button onClick={() => setG({ buckets: g.buckets.filter((_, j) => j !== bi) })} style={{ ...btn, color: ui.red }}>×</button>
                </div>
              ))}
              <div style={row}>
                <button onClick={() => setG({ buckets: [...g.buckets, { id: `k${Date.now().toString(36)}`, label: '', zql: '' }] })} style={btn}>+ Add a bucket</button>
                <input value={g.other} onChange={(e) => setG({ other: e.target.value })} placeholder="Header for the rest — “(other)”" style={{ ...small, flex: '0 1 220px' }} />
                <span style={S.tiny}>{test}</span>
              </div>
              <div style={{ ...S.tiny, marginBottom: 8 }}>Each bucket is one query over the structure’s work items, answered by the server like a quick button; headers keep this order.</div>
            </>
          ) : null}
          {g.kind === 'formula' ? (
            <>
              <div style={lbl}>Formula — JavaScript over one work item; returns the group</div>
              <textarea value={g.formula} onChange={(e) => setG({ formula: e.target.value })} rows={4} spellCheck={false}
                placeholder={'month(date("Resolved"))\n// or: daysUntil("duedate") < 0 ? bucket("late", "Late", 0) : bucket("ok", "On time", 1)'}
                style={{ ...mono, width: '100%', resize: 'vertical', lineHeight: 1.45, marginBottom: 6 }} />
              <div style={{ ...S.tiny, marginBottom: 8 }}>
                {FORMULA_HELP.map(([sig, what2]) => <div key={sig}><code style={{ color: ui.fg }}>{sig}</code> — {what2}</div>)}
                <div>Return a string, a number, a date, or <code>bucket(…)</code>. An error becomes a group of its own, so nothing goes blank.</div>
              </div>
            </>
          ) : null}
          {/* THE HEADERS' ICON, when the values name work items of one kind
              but are only text (an Asset dropdown): the admin names the type. */}
          <div style={lbl}>Header icon (optional)</div>
          {(() => {
            // A field named like a work type ("Asset") wears that type's icon
            // without being asked — say so, and let the admin change or stop it.
            const auto = g.kind === 'field' && g.field && workTypes ? workTypes.get(String(g.field).trim().toLowerCase()) : null;
            // a grouping by project wears each project's picture unless told "None"
            const byProject = g.kind === 'preset' && g.preset === 'project';
            const names = workTypes ? [...new Set([...workTypes.values()].map((t) => t && t.name).filter(Boolean))].sort((a, b) => a.localeCompare(b)) : [];
            if (g.iconType && g.iconType !== '-' && !names.includes(g.iconType)) names.unshift(g.iconType);
            return (
              <>
                <select value={g.iconType || ''} onChange={(e) => setG({ iconType: e.target.value })} style={{ ...small, marginBottom: 4, width: 360 }}>
                  <option value="">{auto && auto.name ? `Automatic — ${auto.name} (the field’s own name)` : byProject ? 'Automatic — each project’s picture' : '— none —'}</option>
                  {auto || byProject ? <option value="-">None{byProject ? ' — no project pictures' : ''}</option> : null}
                  {names.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div style={{ ...S.tiny, marginBottom: 8 }}>
                  Every header of this grouping wears that work type’s icon — for a field whose values are the names of
                  work items of one kind (an Asset dropdown → the Asset icon). A field named like a work type does this
                  on its own. Work type and status groupings wear their own.
                </div>
              </>
            );
          })()}
          {sample ? (
            <div style={{ ...S.tiny, marginBottom: 8, padding: '6px 9px', borderRadius: 6, background: ui.surface }}>
              For <strong style={{ color: ui.fg }}>{sample.key}</strong> this groups under: <strong style={{ color: ui.fg }}>{preview || '(none)'}</strong>
            </div>
          ) : null}
          <div style={row}>
            <button onClick={() => remove()} style={{ ...btn, color: ui.red }}>Remove this grouping</button>
            <button onClick={() => setFolder({ groups: [...folder.groups, { id: `g${Date.now().toString(36)}`, kind: 'field', field: '' }] })} style={btn}>
              + Another grouping under it
            </button>
          </div>
        </>
      );
    }
  } else if (what === 'folder') {
    title = 'Folder';
    // WHERE THE FOLDER SITS: the top of the structure or inside another
    // folder — never itself, never one of its own sub-folders. The same move
    // a drag of the folder's row makes, for a keyboard or a phone.
    const cannotHold = descendantFolderIds(draft.folders, folder.id);
    cannotHold.add(folder.id);
    body = (
      <>
        <div style={lbl}>Name</div>
        <input value={folder.name} onChange={(e) => setFolder({ name: e.target.value })} style={{ ...small, marginBottom: 8 }} />
        <div style={lbl}>Icon</div>
        <div style={{ ...row, gap: 4 }}>
          {Object.entries(FOLDER_ICONS).map(([name, glyph]) => (
            <button key={name} title={name} onClick={() => setFolder({ icon: name })}
              style={{ ...btn, fontSize: 15, padding: '2px 6px', outline: folder.icon === name ? `2px solid ${ui.amber}` : 'none' }}>{glyph}</button>
          ))}
        </div>
        <div style={row}>
          <input value={FOLDER_ICONS[folder.icon] ? '' : (folder.icon || '')} onChange={(e) => setFolder({ icon: e.target.value.trim() || 'folder' })}
            placeholder="…or any emoji (💯 🔎 🛒), or a picture’s address (https://…)" style={{ ...small, flex: '1 1 260px' }} />
          <span style={{ fontSize: 16 }}>{iconSpec(folder.icon).src ? <img src={iconSpec(folder.icon).src} alt="" width={16} height={16} /> : iconSpec(folder.icon).text}</span>
        </div>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center', marginBottom: 8 }}>
          <input type="checkbox" checked={!!folder.bare} onChange={(e) => setFolder({ bare: e.target.checked })} />
          Bare — readers see what it holds without the folder row (rules at the top of the structure)
        </label>
        <div style={lbl}>Inside</div>
        <select value={folder.parentId || ''} onChange={(e) => setFolder({ parentId: e.target.value || null })} style={{ ...small, marginBottom: 8, width: 320 }}>
          <option value="">— the top of the structure —</option>
          {draft.folders.filter((x) => !cannotHold.has(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <div style={row}>
          <button onClick={() => { setExpand({ links: [...folder.expand.links, newLink()] }); }} style={btn}>+ Link extender</button>
          <button onClick={() => setFolder({ groups: [...folder.groups, { id: `g${Date.now().toString(36)}`, kind: 'field', field: '' }] })} style={btn}>+ Grouping</button>
        </div>
        {removeBtn('Remove this folder', () => remove())}
      </>
    );
  }

  return createPortal((
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(0,0,0,0.28)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '9vh' }}>
      <div ref={ref} style={{
        width: 'min(620px, 94vw)', maxHeight: '78vh', overflowY: 'auto', background: ui.card,
        border: `1px solid ${ui.amber}`, borderRadius: ui.radius, boxShadow: ui.shadowSm, padding: '14px 16px', fontFamily: FONT, color: ui.fg,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ color: ui.amber, fontSize: 13 }}>✎</span>
          <div style={{ fontSize: 14, fontWeight: 800, flex: '1 1 auto' }}>{title}</div>
          <button onClick={onClose} style={{ ...btn }}>Close</button>
        </div>
        {body}
        <div style={{ ...S.tiny, marginTop: 12, borderTop: `1px solid ${ui.border}`, paddingTop: 8 }}>
          The structure rebuilds as you change this. Use <strong>Save</strong> in Page settings to keep it for everybody.
        </div>
      </div>
    </div>
  ), document.body);
}
